import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Dialog, DialogContent, IconButton, Typography, alpha } from "@mui/material";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import { getMetaSocialFromMessage, storyExpiredPlaceholder } from "../../utils/metaMessageSocial";
import { downloadChatMedia, filenameFromMediaUrl } from "../../utils/downloadChatMedia";

const fullscreenVideoSx = {
  "&:fullscreen, &:-webkit-full-screen": {
    width: "100% !important",
    height: "100% !important",
    maxWidth: "none !important",
    maxHeight: "none !important",
    borderRadius: "0 !important",
    objectFit: "contain",
    backgroundColor: "#000",
  },
};

function isAppleHandheld() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return navigator.platform === "MacIntel" && Number(navigator.maxTouchPoints) > 1;
}

function exitNativeFullscreen(videoEl) {
  const fs = document.fullscreenElement || document.webkitFullscreenElement;
  if (fs === videoEl) {
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    try {
      exit?.call(document);
    } catch {
      /* ignore */
    }
  }
  try {
    videoEl?.webkitExitFullscreen?.();
  } catch {
    /* ignore */
  }
}

const VideoMessage = ({ message }) => {
  const { t } = useTranslation("chat");
  const [videoError, setVideoError] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [open, setOpen] = useState(false);
  const inlineRef = useRef(null);
  const previewRef = useRef(null);
  const pendingTimeRef = useRef(0);
  const pendingPlayRef = useRef(true);
  const src = message?.video || message?.url || "";
  const metaSocial = getMetaSocialFromMessage(message);
  const expiredText = storyExpiredPlaceholder(metaSocial);

  useEffect(() => {
    setVideoError(false);
    setPreviewError(false);
    setOpen(false);
  }, [src]);

  const openPreview = useCallback(() => {
    const inline = inlineRef.current;
    pendingTimeRef.current = Number.isFinite(inline?.currentTime) ? inline.currentTime : 0;
    pendingPlayRef.current = true;
    try {
      if (inline && !inline.paused) inline.pause();
    } catch {
      /* jsdom / autoplay policies */
    }
    setPreviewError(false);
    setOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    const preview = previewRef.current;
    const inline = inlineRef.current;
    if (preview && inline) {
      try {
        inline.currentTime = preview.currentTime;
      } catch {
        /* ignore */
      }
      try {
        preview.pause();
      } catch {
        /* jsdom / autoplay policies */
      }
    }
    setOpen(false);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const preview = previewRef.current;
    if (!preview) return undefined;

    const apply = () => {
      try {
        preview.currentTime = pendingTimeRef.current || 0;
      } catch {
        /* ignore */
      }
      if (pendingPlayRef.current) {
        try {
          const playResult = preview.play();
          if (playResult && typeof playResult.catch === "function") {
            playResult.catch(() => {});
          }
        } catch {
          /* jsdom / autoplay policies */
        }
      }
    };

    if (preview.readyState >= 1) {
      apply();
      return undefined;
    }
    preview.addEventListener("loadedmetadata", apply, { once: true });
    return () => preview.removeEventListener("loadedmetadata", apply);
  }, [open]);

  useEffect(() => {
    const el = inlineRef.current;
    if (!el) return undefined;

    const redirectToPreview = () => {
      const fs = document.fullscreenElement || document.webkitFullscreenElement;
      if (fs !== el) return;
      exitNativeFullscreen(el);
      openPreview();
    };

    const onWebkitBegin = () => {
      if (isAppleHandheld()) return;
      exitNativeFullscreen(el);
      openPreview();
    };

    el.addEventListener("fullscreenchange", redirectToPreview);
    el.addEventListener("webkitfullscreenchange", redirectToPreview);
    el.addEventListener("webkitbeginfullscreen", onWebkitBegin);
    return () => {
      el.removeEventListener("fullscreenchange", redirectToPreview);
      el.removeEventListener("webkitfullscreenchange", redirectToPreview);
      el.removeEventListener("webkitbeginfullscreen", onWebkitBegin);
    };
  }, [src, videoError, openPreview]);

  if (!src || !String(src).trim()) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        gap: 0.5,
        width: "100%",
        maxWidth: 400,
      }}
    >
      {videoError ? (
        <>
          <VideocamOffIcon sx={{ fontSize: 60, color: "gray" }} />
          {expiredText ? (
            <Typography variant="caption" color="text.secondary" align="center" sx={{ px: 1 }}>
              {expiredText}
            </Typography>
          ) : null}
        </>
      ) : (
        <Box sx={{ position: "relative", width: "100%" }}>
          <Box
            component="video"
            ref={inlineRef}
            className="chat-video-player"
            controls
            playsInline
            preload="metadata"
            controlsList="nofullscreen"
            src={src}
            onError={() => setVideoError(true)}
            sx={{
              display: "block",
              width: "100%",
              maxHeight: 320,
              borderRadius: 1,
              backgroundColor: "#000",
              ...fullscreenVideoSx,
            }}
          >
            Seu navegador não suporta vídeos.
          </Box>
          <IconButton
            onClick={() => downloadChatMedia(src, filenameFromMediaUrl(src, "video.mp4"))}
            aria-label={t("webchat.downloadAria")}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 48,
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={openPreview}
            aria-label="Ampliar vídeo"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Dialog
        open={open}
        onClose={closePreview}
        maxWidth={false}
        aria-label="Visualização do vídeo"
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
            overflow: "visible",
            maxWidth: "calc(100dvw - 24px)",
            maxHeight: "calc(100dvh - 24px)",
            m: 1.5,
          },
        }}
        slotProps={{
          backdrop: {
            sx: { bgcolor: alpha("#000", 0.88) },
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            overflow: "visible",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "transparent",
          }}
        >
          <IconButton
            onClick={() => downloadChatMedia(src, filenameFromMediaUrl(src, "video.mp4"))}
            aria-label={t("webchat.downloadAria")}
            sx={{
              position: "fixed",
              top: 12,
              right: 60,
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={closePreview}
            aria-label="Fechar vídeo"
            sx={{
              position: "fixed",
              top: 12,
              right: 12,
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <CloseIcon />
          </IconButton>

          {!previewError ? (
            <Box
              component="video"
              ref={previewRef}
              className="chat-video-player"
              controls
              playsInline
              autoPlay
              src={src}
              onError={() => setPreviewError(true)}
              sx={{
                maxWidth: "calc(100dvw - 24px)",
                maxHeight: "calc(100dvh - 24px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
                backgroundColor: "#000",
                borderRadius: 1,
                ...fullscreenVideoSx,
              }}
            >
              Seu navegador não suporta vídeos.
            </Box>
          ) : (
            <VideocamOffIcon sx={{ fontSize: 80, color: "grey.500" }} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default VideoMessage;
